import axiosServices from "../utils/axios";
import { auditBerths } from "../utils/berth-utils";
import { convertToCamelCase } from "../utils/code-utils";

import type {
  BerthData,
  BerthInput,
  ScheduleBunkers,
  ScheduleDeliveries,
  ScheduleOnBoardService,
  ScheduleOperations,
} from "../utils/types/voyageSchedule";

interface ScheduleApiData {
  berth_id: string;
  job_id: string;
  eta: string;
  etd: string;
  status: string;
  prospect_notes: string | null;
  order: number;
  status_pick_list: {
    value: string;
  } | null;
  berth_location: {
    name: string;
    type: {
      value: string;
    } | null;
    port_calling: {
      name: string;
    } | null;
  } | null;
  lock_fact: boolean;
  shifting_hours: number;
  onsite_duration: number;
  is_error: boolean;
  error_info?: {
    errorScore: number;
    errors: string;
    next: boolean;
  }[];

  cargos: Array<any>;
}
interface ScheduleApiResponse {
  data: ScheduleApiData[];
}

interface UpdateLockFactApiResponse {
  data: {
    values: ScheduleApiData[];
  };
  message: string;
}

interface ScheduleDetailsApiResponse {
  data: {
    berth_id: string;
    operations: ScheduleOperations[] | null;
    deliveries: ScheduleDeliveries[] | null;
    on_board_services: ScheduleOnBoardService[] | null;
    bunkers: ScheduleBunkers[] | null;
  };
}

interface ScheduleCompleteDetailsApiResponse {
  data: BerthData;
}

export const transformData = (data: ScheduleApiData[]) => {
  const schedultDatas = data.map((schedule) => ({
    appointment_id: schedule.berth_id,
    id: schedule.berth_id,
    berthId: schedule.berth_id,
    stops: schedule.berth_location?.name || "",
    type: schedule.berth_location?.type?.value || "",
    status: schedule.status,
    port: schedule.berth_location?.port_calling?.name || "",
    onsite: schedule.onsite_duration,
    shifting: schedule.shifting_hours,
    eta: schedule.eta,
    etd: schedule.etd,
    order: schedule.order,
    cargoQuantity: schedule.cargos?.length || 0,
    prospect_notes: schedule.prospect_notes,
    lockFact: schedule.lock_fact,
    isError: schedule.is_error,
    berth_location: schedule.berth_location,
    status_pick_list: schedule.status_pick_list,
    error_info: schedule.error_info,
    jobId: schedule.job_id,
  }));
  return auditBerths(schedultDatas);
};

export const fetchScheduleData = async (id: string) => {
  const response = await axiosServices.get<ScheduleApiResponse>(
    "/berthrotation",
    {
      params: {
        jobId: id,
      },
    }
  );
  return transformData(response.data.data);
};

export const updateLockFact = async (
  id: string,
  data: {
    berth_id: string;
    lock_fact: boolean;
  }
) => {
  const response = await axiosServices.put<UpdateLockFactApiResponse>(
    `/berthrotation/${data.berth_id}`,
    data,
    {
      params: {
        jobId: id,
      },
    }
  );
  return transformData(response.data.data.values);
};

export const updateBerthRotation = async (
  berth_id: string,
  job_id: string,
  data: {
    [key: string]: any;
  }
) => {
  const payload = {
    ...data,
    ...(data.berth_location_id && { berth_id }), // add berth_id only if berth_location_id is present
  };

  const response = await axiosServices.put(
    `/berthrotation/${berth_id}`,
    payload,
    {
      params: { jobId: job_id },
    }
  );

  return response.data.data;
};

export const updatePosition = async (
  id: string,
  data: {
    berthId: string;
    direction: "down" | "up";
    startIndex: number;
  }
) => {
  const response = await axiosServices.post<ScheduleApiResponse>(
    "/berthrotation/calculate",
    data,
    {
      params: {
        jobId: id,
      },
    }
  );
  return transformData(response.data.data);
};

export const deleteScheduleData = async (id: string, berthId: string) => {
  const response = await axiosServices.delete(`/berthrotation/${berthId}`, {
    params: {
      jobId: id,
    },
  });
  return { ...response, berthId };
};

export const addNewBerth = async (id: string, data: BerthInput) => {
  const response = await axiosServices.post<{ data: ScheduleApiData }>(
    `/berthrotation`,
    data,
    {
      params: {
        jobId: id,
      },
    }
  );
  return transformData([response.data.data]);
};

export const fetchScheduleDetails = async (berthId: string) => {
  const response = await axiosServices.get<ScheduleDetailsApiResponse>(
    `/berthrotation/${berthId}`,
    {
      params: {
        format: "details",
      },
    }
  );
  return response.data.data;
};

export const fetchScheduleCompleteDetails = async (berthId: string) => {
  const response = await axiosServices.get<ScheduleCompleteDetailsApiResponse>(
    `/berthrotation/${berthId}`
  );
  return convertToCamelCase<BerthData>(response.data.data);
};
